import { formatDecimalDisplay } from '../formUtils';
import { airStreamOptions } from '../airStreamOptions';
import { useAirStreamOfferedIds } from '../useAirStreamOfferedIds';
import type { ICmsAhuDoc } from 'models';
import { useGetMasterListByUrlQuery } from 'app/services/ahuComponentFormsApi';
import {
  BareInput,
  BareSelect,
  CatalogueChrome,
  CheckboxRow,
  cmsList,
  InputRow,
  SelectRow,
  type OptionRow,
} from '../formKit';
import type { ComponentFormApi } from '../useComponentForm';
import type { CatalogueFieldConfig } from '../componentFormTypes';

/**
 * Phase 6e2a — shared form-body blocks over the §52.1 kit, re-cut against the
 * per-type tmpls after verify round 1 (6e2a-4/-5/-8): paired rows render in
 * ONE li with the legacy div classes, headings carry the per-type
 * vis-classes + hidden wrappers, and the performance block follows the
 * filter/eliminator tmpl grammar (Air Stream & Flow combo, Face Velocity,
 * Air PD Initial | Final, EDB|EWB, LDB|LWB).
 */

export const PRICING_METHODS_URL = '/api/v1.0/master/v1.0.0/ahu/pricing_methods/';

export const usePricingMethods = (): OptionRow[] => {
  const { data } = useGetMasterListByUrlQuery(PRICING_METHODS_URL);
  return (data ?? []) as OptionRow[];
};

export const cmsAirStreams = (cms: ICmsAhuDoc | undefined): OptionRow[] =>
  cmsList(cms as Record<string, unknown> | undefined, 'air.air_stream');

/**
 * The air streams a component may be put on: the ones its AHU's construction
 * class offers, plus whatever the record already holds. An air stream is
 * never cleared from a component when its class withdraws it, so the row has
 * to be able to show its own saved value -- the same rule the accessory
 * dialogs use.
 */
export const useAirStreamRows = (
  cms: ICmsAhuDoc | undefined, savedId: unknown
): OptionRow[] => airStreamOptions(
  cmsAirStreams(cms),
  useAirStreamOfferedIds(),
  savedId as number | string | null | undefined
) as OptionRow[];

/**
 * The same rule for a form with SEVERAL air-stream selects.
 *
 * ★ ONE HOOK CALL, ONE LIST PER SELECT. A wheel panel has four selects and a
 * plate-heat-exchanger panel eight, so `useAirStreamRows` per select would be
 * a hook per select -- and the hazard is not the count (it is fixed) but
 * WHERE they end up: these forms early-return a preloader, and a hook written
 * below that return is React #310, which blanked the whole board twice on
 * this branch. So the hook is called once, HERE, and what comes back is a
 * plain function the form may call anywhere, as many times as it likes.
 *
 * Per select and not per form, because grandfathering is a statement about a
 * FIELD: one shared list put the wheel's Exhaust into its Outside dropdown
 * (AHU 7241). `scripts/check-hook-order.py` guards the placement.
 */
export const useAirStreamRowsFor = (
  cms: ICmsAhuDoc | undefined
): ((savedId: unknown) => OptionRow[]) => {
  const offered = useAirStreamOfferedIds();
  return (savedId: unknown) => airStreamOptions(
    cmsAirStreams(cms), offered,
    savedId as number | string | null | undefined
  ) as OptionRow[];
};

// ---------------------------------------------------------------------------
// heading (component-form-heading grammar — per-tmpl census, 6e2a-8)
// ---------------------------------------------------------------------------
export interface HeadingChromeSpec {
  /** legacy frame chrome gates the count span on model.has_frame. */
  countVisible?: boolean;
  /** the catalogue field whose counts feed the badge. */
  field: CatalogueFieldConfig;
  /** verbatim button vis class (vis-eliminator / vis-fan ...). */
  visClass: string;
  /** verbatim extra button classes ('rating hide', 'hide', 'mr-30'). */
  buttonExtraClass?: string;
  showSmartlist?: boolean;
  showReset?: boolean;
  searchResultClass?: string;
}

export const HeadingChrome = ({
  api,
  spec,
  wrapperClass,
}: {
  api: ComponentFormApi;
  spec: HeadingChromeSpec;
  wrapperClass?: string;
}) => {
  const engine = api.catalogueFields[spec.field.targetField];
  if (!engine) return null;
  return (
    <CatalogueChrome
      componentName={spec.field.componentName}
      componentsName={spec.field.componentsName}
      count={engine.cmsCount}
      fullCount={engine.cmsFullCount}
      visClass={spec.visClass}
      buttonExtraClass={spec.buttonExtraClass}
      wrapperClass={wrapperClass}
      showSmartlist={spec.showSmartlist ?? true}
      showReset={spec.showReset ?? true}
      countVisible={spec.countVisible ?? true}
      searchResultClass={spec.searchResultClass ?? 'search-result'}
      onToggleList={() => void engine.toggleSmartList()}
      onReset={() => engine.deselectModel()}
    />
  );
};

/** The §13 rows 6/7 Rating radio + label block (withRating tmpls only). */
export const RatingModeBlock = ({
  api,
  dataName,
  wrapperClass,
}: {
  api: ComponentFormApi;
  dataName: string;
  wrapperClass?: string;
}) => (
  <div className={wrapperClass}>
    <div>
      <input
        type="radio"
        name="select_mode_id"
        className="select_mode_radio hidden"
        data-id="2"
        data-value="rating"
        data-name={dataName}
        readOnly
        checked
      />
      <label
        className="active-mode rate-active-mode"
        onClick={() => api.openRatingStub()}
      >
        Rating
      </label>
    </div>
  </div>
);

export const FormHeading = ({
  title,
  api,
  /** wrapper classes verbatim from the tmpl (e.g.
   * 'component-form-heading clearfix rating-mode hide'). */
  wrapperClass = 'component-form-heading clearfix',
  chrome,
  chromeWrapperClass,
  withRatingBlock,
  ratingBlockDataName,
  ratingBlockWrapperClass = 'ahu-header-btns right',
  h2Class = 'left',
}: {
  title: React.ReactNode;
  api: ComponentFormApi;
  wrapperClass?: string;
  chrome?: HeadingChromeSpec[];
  chromeWrapperClass?: string;
  withRatingBlock?: boolean;
  ratingBlockDataName?: string;
  ratingBlockWrapperClass?: string;
  h2Class?: string;
}) => (
  <div className={wrapperClass}>
    <h2 className={h2Class}>{title}</h2>
    {(chrome ?? []).map((spec) => (
      <HeadingChrome
        key={spec.field.targetField}
        api={api}
        spec={spec}
        wrapperClass={chromeWrapperClass}
      />
    ))}
    {withRatingBlock && (
      <RatingModeBlock
        api={api}
        dataName={ratingBlockDataName ?? ''}
        wrapperClass={ratingBlockWrapperClass}
      />
    )}
  </div>
);

// ---------------------------------------------------------------------------
// field wrappers bound to the chassis commit functions
// ---------------------------------------------------------------------------
interface CommonFieldProps {
  api: ComponentFormApi;
  name: string;
  label: React.ReactNode;
  unit?: string;
  required?: boolean;
}

export const MSelect = ({
  api,
  name,
  label,
  unit,
  required,
  options,
  cmsPath,
  labelKey,
  valueKey,
  disabled,
  modelField,
  rowClass,
  extraClass,
  selectionClass,
  hideNone,
  children,
  onChangeExtra,
}: CommonFieldProps & {
  options?: OptionRow[];
  /** dotted path into componentCms (§49.3 template data). */
  cmsPath?: string;
  labelKey?: string;
  valueKey?: string;
  disabled?: boolean;
  /** read the value from another model attr (paired selects). */
  modelField?: string;
  rowClass?: string;
  /** size classes appended to the field class (medium-input etc.). */
  extraClass?: string;
  selectionClass?: string;
  hideNone?: boolean;
  children?: React.ReactNode;
  /** W4 — extra side-effect after the model commit (e.g. the PHX form's
   * dependency-graph re-cascade on a parent change). Optional. */
  onChangeExtra?: (raw: string) => void;
}) => (
  <SelectRow
    name={name}
    fieldClass={`model_field${extraClass ? ` ${extraClass}` : ''}`}
    value={api.model?.get(modelField ?? name)}
    options={options ?? cmsList(api.componentCms, cmsPath ?? name)}
    labelKey={labelKey}
    valueKey={valueKey}
    label={label}
    unit={unit}
    required={required}
    disabled={disabled}
    rowClass={rowClass}
    selectionClass={selectionClass}
    hideNone={hideNone}
    onChange={(raw) => {
      api.commitModelField(modelField ?? name, raw);
      onChangeExtra?.(raw);
    }}
  >
    {children}
  </SelectRow>
);

export const MInput = ({
  api,
  name,
  label,
  unit,
  required,
  formatter = 'decimal',
  readOnly,
  extraClass,
  rowClass,
  min,
  max,
  children,
}: CommonFieldProps & {
  formatter?: 'decimal' | 'integer' | 'none';
  readOnly?: boolean;
  extraClass?: string;
  rowClass?: string;
  min?: number;
  max?: number;
  children?: React.ReactNode;
}) => (
  <InputRow
    name={name}
    fieldClass="model_field"
    value={api.model?.get(name)}
    label={label}
    unit={unit}
    formatter={formatter === 'none' ? undefined : formatter}
    required={required}
    readOnly={readOnly}
    extraClass={extraClass}
    rowClass={rowClass}
    min={min}
    max={max}
    onCommit={(v) => api.commitModelField(name, v)}
  >
    {children}
  </InputRow>
);

export const MCheck = ({
  api,
  name,
  label,
  disabled,
}: CommonFieldProps & { disabled?: boolean }) => (
  <CheckboxRow
    name={name}
    fieldClass="model_field"
    checked={!!api.model?.get(name)}
    label={label}
    disabled={disabled}
    onCommit={(checked) => api.commitModelCheckbox(name, checked)}
  />
);

/** Bare model-bound input for legacy multi-control rows (no li). */
export const MBareInput = ({
  api,
  name,
  valueName,
  formatter = 'decimal',
  readOnly,
  extraClass,
  fieldClass = 'model_field',
  min,
  max,
  style,
}: {
  api: ComponentFormApi;
  name: string;
  /** read the display value off a DIFFERENT model field (legacy bhp/bp_kw swap). */
  valueName?: string;
  /** 'none' = legacy input without data-formatter_type (raw value). */
  formatter?: 'decimal' | 'integer' | 'none';
  readOnly?: boolean;
  extraClass?: string;
  fieldClass?: string;
  /** legacy data-min / data-max (the evap airflow input clamps to the row's
   * min_airflow / max_airflow — form.tmpl :532). */
  min?: number;
  max?: number;
  /** legacy inline style (filter form.tmpl:171 `style="margin-left:5px"`). */
  style?: React.CSSProperties;
}) => (
  <BareInput
    name={name}
    fieldClass={fieldClass}
    value={api.model?.get(valueName ?? name)}
    formatter={formatter === 'none' ? undefined : formatter}
    readOnly={readOnly}
    extraClass={extraClass}
    min={min}
    max={max}
    style={style}
    onCommit={(v) => api.commitModelField(name, v)}
  />
);

/** Bare model-bound select for legacy multi-control rows (no li). */
export const MBareSelect = ({
  api,
  name,
  options,
  cmsPath,
  labelKey,
  valueKey,
  extraClass,
  selectionClass,
  selectionStyle,
  selectStyle,
  disabled,
  hideNone,
  noneMode,
}: {
  api: ComponentFormApi;
  name: string;
  options?: OptionRow[];
  cmsPath?: string;
  labelKey?: string;
  valueKey?: string;
  extraClass?: string;
  selectionClass?: string;
  /** legacy inline styles (filter form.tmpl:164-165 `style="min-width: 200px"`
   * on BOTH the selection mirror and the select). */
  selectionStyle?: React.CSSProperties;
  selectStyle?: React.CSSProperties;
  disabled?: boolean;
  hideNone?: boolean;
  /** legacy None-option mode — the eliminator convention / supplied_by
   * selects render `<option value="None">- -</option>` with NO `hide`. */
  noneMode?: 'visible' | 'hidden' | 'omit';
}) => (
  <BareSelect
    name={name}
    fieldClass={`model_field${extraClass ? ` ${extraClass}` : ''}`}
    value={api.model?.get(name)}
    options={options ?? cmsList(api.componentCms, cmsPath ?? name)}
    labelKey={labelKey}
    valueKey={valueKey}
    disabled={disabled}
    hideNone={hideNone}
    noneMode={noneMode}
    selectionClass={selectionClass}
    selectionStyle={selectionStyle}
    selectStyle={selectStyle}
    onChange={(raw) => api.commitModelField(name, raw)}
  />
);

/** Catalogue params select — options from componentCms, value from the
 * field's live filter, commit → results_filter (§49.4 binding). */
export const PSelect = ({
  api,
  target,
  name,
  label,
  unit,
  required,
  cmsPath,
  labelKey,
  valueKey,
  options,
  rowClass,
  extraClass,
  selectionClass,
  disabled,
  noneMode,
  children,
}: {
  api: ComponentFormApi;
  /** owning catalogue field's targetField. */
  target: string;
  name: string;
  label: React.ReactNode;
  unit?: string;
  required?: boolean;
  cmsPath?: string;
  labelKey?: string;
  valueKey?: string;
  options?: OptionRow[];
  rowClass?: string;
  extraClass?: string;
  selectionClass?: string;
  disabled?: boolean;
  /** legacy None-option mode. The params tmpls render `- -` WITHOUT
   * `class="hide"` (gas_heater form.tmpl:37 and every sibling), so it is a
   * real list entry; the kit's default hides it, which also trips
   * skeleton.css:1424 `select:not(:has(option:not(.hide)))` — half opacity —
   * on any select whose CMS list came back empty. */
  noneMode?: 'visible' | 'hidden' | 'omit';
  children?: React.ReactNode;
}) => {
  const engine = api.catalogueFields[target];
  const compName = engine?.config.componentName ?? 'component';
  return (
    <SelectRow
      name={name}
      fieldClass={`${compName}_params_field${extraClass ? ` ${extraClass}` : ''}`}
      value={engine?.paramValue(name)}
      options={options ?? cmsList(api.componentCms, cmsPath ?? name)}
      labelKey={labelKey}
      valueKey={valueKey}
      label={label}
      unit={unit}
      required={required}
      disabled={disabled || api.filterFieldsDisabled}
      noneMode={noneMode}
      rowClass={rowClass}
      selectionClass={selectionClass}
      onChange={(raw) => api.commitParamsField(target, name, raw)}
    >
      {children}
    </SelectRow>
  );
};

/** Bare params select (legacy paired params rows — MERV | Efficiency). */
export const PBareSelect = ({
  api,
  target,
  name,
  cmsPath,
  labelKey,
  valueKey,
  options,
  extraClass,
  selectionClass,
  selectionStyle,
  disabled,
}: {
  api: ComponentFormApi;
  target: string;
  name: string;
  cmsPath?: string;
  labelKey?: string;
  valueKey?: string;
  options?: OptionRow[];
  extraClass?: string;
  selectionClass?: string;
  selectionStyle?: React.CSSProperties;
  disabled?: boolean;
}) => {
  const engine = api.catalogueFields[target];
  const compName = engine?.config.componentName ?? 'component';
  return (
    <BareSelect
      name={name}
      fieldClass={`${compName}_params_field${extraClass ? ` ${extraClass}` : ''}`}
      value={engine?.paramValue(name)}
      options={options ?? cmsList(api.componentCms, cmsPath ?? name)}
      labelKey={labelKey}
      valueKey={valueKey}
      disabled={disabled || api.filterFieldsDisabled}
      selectionClass={selectionClass}
      selectionStyle={selectionStyle}
      onChange={(raw) => api.commitParamsField(target, name, raw)}
    />
  );
};

// ---------------------------------------------------------------------------
// repeated tmpl groups
// ---------------------------------------------------------------------------
/** "Air Stream & Flow" combo — ONE li: stream select + airflow input
 * (filter: div.single-line; eliminator: div.first-two-line; fan: div). */
export const AirStreamFlow = ({
  api,
  cms,
  airStreamField = 'air_stream_id',
  rowClass = 'single-line',
}: {
  api: ComponentFormApi;
  cms: ICmsAhuDoc | undefined;
  airStreamField?: string;
  rowClass?: string;
}) => (
  <li>
    <div className={rowClass}>
      <span className="unit">CFM</span>{' '}
      <MBareSelect
        api={api}
        name={airStreamField}
        options={useAirStreamRows(cms, api.model?.get(airStreamField))}
        extraClass="medium-input m-0"
      />{' '}
      <MBareInput api={api} name="airflow_actual" formatter="integer" extraClass="medium-input" />{' '}
      <span className="label">Air Stream &amp; Flow</span>
    </div>
  </li>
);

/**
 * Performance block per the filter/eliminator tmpl grammar:
 *  - Face Velocity: classless div, NON-model input, readonly;
 *  - "Air PD Initial | Final" pair (div `<type>-air-final`).
 *
 * Dev-sync J6: the EDB|EWB / LDB|LWB rows that used to follow are GONE from
 * this shared block — the season wave REPLACED them per-template:
 *  - filter form.tmpl −26: both (previously `class="hide"`) rows deleted
 *    outright, nothing added (its season UI lives in the SELECTOR);
 *  - eliminator/silencer: replaced by the Season select + the gated
 *    Summer/Winter groups — rendered by the caller via
 *    `<ComponentSeasonBlock>` (forms/SeasonGroup.tsx), mirroring the legacy
 *    diff which patched each form template individually.
 */
export const PerformanceCommon = ({
  api,
  typePrefix,
  withFinalApd = true,
}: {
  api: ComponentFormApi;
  /** 'filter' | 'eliminator' — feeds the legacy div classes. */
  typePrefix: string;
  withFinalApd?: boolean;
}) => (
  <>
    <li>
      <div>
        <span className="unit">fpm</span>{' '}
        <input
          type="text"
          data-formatter_type="decimal"
          autoComplete="off"
          name="face_velocity_actual"
          value={formatDecimalDisplay(api.model?.get('face_velocity_actual'))}
          readOnly
          tabIndex={-1}
          onChange={() => undefined}
        />{' '}
        <span className="label">Face Velocity</span>
      </div>
    </li>
    <li>
      <div className={`${typePrefix}-air-final`}>
        <span className="unit">in WC</span>{' '}
        <MBareInput api={api} name="initial_apd" readOnly extraClass="medium-input" />{' '}
        {withFinalApd && (
          <>
            <MBareInput api={api} name="final_recommended_apd" extraClass="medium-input" />{' '}
          </>
        )}
        <span className="label">
          <span>Air PD Initial </span> | <span>Final</span>
        </span>
      </div>
    </li>
  </>
);

/** .section_field input (single-section only — §49.3). */
export const SInput = ({
  api,
  name,
  label,
  unit,
  value,
  rowClass,
}: CommonFieldProps & { value?: unknown; rowClass?: string }) => (
  <InputRow
    name={name}
    fieldClass="section_field"
    value={value}
    label={label}
    unit={unit}
    formatter="decimal"
    rowClass={rowClass}
    onCommit={(v) => api.commitSectionField(name, v)}
  />
);

/** Bare .section_field input for paired temp rows. */
export const SBareInput = ({
  api,
  name,
  value,
  readOnly,
  min,
  max,
  placeholder,
}: {
  api: ComponentFormApi;
  name: string;
  value?: unknown;
  readOnly?: boolean;
  min?: number;
  max?: number;
  /** J6 — the LDB/LWB placeholder-shows-entering pattern (formKit). */
  placeholder?: unknown;
}) => (
  <BareInput
    name={name}
    fieldClass="section_field medium-input"
    value={value}
    formatter="decimal"
    readOnly={readOnly}
    min={min}
    max={max}
    placeholder={placeholder}
    onCommit={(v) => api.commitSectionField(name, v)}
  />
);

/**
 * Pricing rows per the legacy tmpl: method select + cost pair gated on
 * `pricing_method_id != 1`, with the per-method field names (2 → cost_*_user,
 * 3 → net_price_*_user) and the "<Label> : Mtl | Freight" caption.
 */
export const PricingRows = ({
  api,
  label,
  methodField = 'pricing_method_id',
}: {
  api: ComponentFormApi;
  label: string;
  methodField?: string;
}) => {
  const pricingMethods = usePricingMethods();
  const method = Number(api.model?.get(methodField));
  const costField = method === 3 ? 'net_price_product_user' : 'cost_product_user';
  const freightField = method === 3 ? 'net_price_freight_user' : 'cost_freight_user';
  return (
    <>
      <MSelect api={api} name={methodField} label="Pricing Method" options={pricingMethods} hideNone />
      {method !== 1 && (
        <li>
          <div className="frequency">
            <span className="unit">US$</span>{' '}
            <MBareInput api={api} name={costField} extraClass="text-left medium-input" />{' '}
            <MBareInput api={api} name={freightField} extraClass="text-left medium-input" />{' '}
            <span className="label">{`${label} : Mtl | Freight`}</span>
          </div>
        </li>
      )}
    </>
  );
};

/** Remarks textarea → model.remarks (+ .short_remarks echo lives on the
 * accessory row — component remarks are plain model fields). */
export const RemarksRows = ({ api }: { api: ComponentFormApi }) => (
  <li>
    <div className="single-line">
      <textarea
        name="remarks"
        className="model_field"
        defaultValue={String(api.model?.get('remarks') ?? '')}
        onBlur={(e) => api.commitModelField('remarks', e.target.value)}
      />
      <span className="label">Remarks</span>
    </div>
  </li>
);

/**
 * Blank-off slot — consumer half of `render:blabk_off:<id>` (load-bearing
 * typo, §49.3 renderBlankOffList / parity 307). The shared BlankOffView body
 * (view.blank.off — Part 2 §7.4 wall surface) mounts here in its own cut;
 * the slot re-keys on every event so the 6e2b/wall owner re-renders.
 */
export const BlankOffSlot = ({ api, sectionId }: { api: ComponentFormApi; sectionId: number }) => (
  <li
    key={`blank-off-${sectionId}-${api.blankOffVersion}`}
    className="blank-off-wrapper"
    data-section-id={sectionId}
  />
);

/** Legacy form footer note (eliminator/filter tmpls). */
export const RequiredFieldNote = () => (
  <p className="note">
    <i>*</i> Required Field...
  </p>
);
